import { CommonModule, isPlatformBrowser } from '@angular/common';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectorRef,
  Component,
  Input,
  PLATFORM_ID,
  inject,
  ViewChild,
  ElementRef,
  makeStateKey,
  TransferState,
} from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { DestinationsService } from '../../reducers/services/destinations.service';
import { PropertyCardComponent } from '../../shared/components/cards/property-card/property-card.component';
import { UpcomingPropertyCardComponent } from '../../shared/components/cards/upcoming-property-card/upcoming-property-card.component';
import { HeroBannerComponent } from '../../shared/components/hero-banner/hero-banner.component';
import { SectionTitleComponent } from '../../shared/components/ui-elements/section-title/section-title.component';
import { SmallHeaderComponent } from '../../shared/partials/small-header/small-header.component';
import { HotelListingCardComponent } from '../hotel-listing-card/hotel-listing-card.component';
import { HomeUpcomingPropertiesComponent } from '../home/home-upcoming-properties/home-upcoming-properties.component';
import { MetaTagsService } from '../../reducers/services/meta-tags.service';
import { filter, Subscription } from 'rxjs';
import { JsonLdService } from '../../components/services/json-ld.service';

@Component({
  selector: 'app-hotel-search',
  standalone: true,
  imports: [
    RouterModule,
    CommonModule,
    SmallHeaderComponent,
    HeroBannerComponent,
    HotelListingCardComponent,
    PropertyCardComponent,
    SectionTitleComponent,
    UpcomingPropertyCardComponent,
    HomeUpcomingPropertiesComponent
  ],
  templateUrl: './hotel-search.component.html',
  styleUrl: './hotel-search.component.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class HotelSearchComponent {
  private metaTagsService = inject(MetaTagsService);
  private jsonLd = inject(JsonLdService);

  @ViewChild('heroVideo') heroVideo!: ElementRef<HTMLVideoElement>;
  @Input() set data(value: boolean) {
    this.routeData = value;
    this.destination =
      this.routeData === 'all' ? 'all' : this.routeData?._id || '';
    this.initializeData();
  }

  routeData!: any;
  latestHotels: any[] = [];
  isTocExpanded = false;
  totalCount: number = 0;
  slug: string = '';
  isMuted = true;
  page = 1;
  limit = 9;
  blogContainerId = 'blog-search-container';
  query = '';
  upcominghotels: any[] = [];

  private platformId = inject(PLATFORM_ID);
  destinations: any[] = [];
  loading = false;
  hotelList: any[] = [];
  selectedDestination!: any;
  destination: string = '';
  private routerEventsSubscription: Subscription;
  private readonly SITE_URL = 'https://tripxl.com';
  private readonly LISTING_BASE = `${this.SITE_URL}/our-hotels`;

  constructor(
    private sanitizer: DomSanitizer,
    private router: Router,
    private route: ActivatedRoute,
    private destinationService: DestinationsService,
    private cdr: ChangeDetectorRef,
    private transferState: TransferState
  ) {
    this.routerEventsSubscription = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => {
        if (this.destination === 'all') {
          this.metaTagsService.setDefault(this.getCurrentUrlSafe());
        } else if (this.selectedDestination) {
          this.applyMetaTagsFromDestination(this.selectedDestination);
        }
        this.setHotelListingJsonLd();
      });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe(() => {
      if (this.destination === 'all') {
        this.metaTagsService.setDefault(this.getCurrentUrlSafe());
      }
      this.setHotelListingJsonLd();
    });

    if (isPlatformBrowser(this.platformId)) {
      this.route.paramMap.subscribe((params) => {
        const routeSlug = params.get('slug');

        this.page = 1;

        if (routeSlug) {
          this.slug = routeSlug;
          this.getHotelList(routeSlug);
          this.getDestinationBySlug(routeSlug);
        } else {
          this.slug = '';
          this.destination = 'all';
          this.selectedDestination = null;
          this.metaTagsService.setDefault(this.getCurrentUrlSafe());
          this.getHotelList('');
        }
      });
    } else {
      this.getHotelList(this.slug);
      this.setHotelListingJsonLd();
    }

    if (isPlatformBrowser(this.platformId)) {
      this.getUpcomingHotel();
      this.scrollToTop();
    }
  }

  initializeData() {
    if (this.destination !== 'all') {
      this.getDestinationById(this.destination);
    } else {
      this.selectedDestination = null;
      this.metaTagsService.setDefault(this.getCurrentUrlSafe());
    }

    this.getHotelList(this.slug);
  }

  scrollToTop() {
    if (isPlatformBrowser(this.platformId)) {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  getDestinationById(destinationId: any) {
    this.destinationService.getDestinationById(destinationId).subscribe((res) => {
      this.selectedDestination = res;
      this.applyMetaTagsFromDestination(this.selectedDestination);

      this.setHotelListingJsonLd();

      this.cdr.detectChanges();
      this.reloadHeroVideo();
    });
  }

  fetchHotelsSlug(slug?: string): void {
    this.destinationService.getHotels('', slug, this.page, this.limit).subscribe((res) => {
      const hotels = res?.data;
      this.totalCount = res?.totalCount;
      this.latestHotels = [...this.latestHotels, ...hotels];
      this.page++;
    });
  }

  scrollToSection(sectionId: string): void {
    if (!isPlatformBrowser(this.platformId)) return;
    const section = document.getElementById(sectionId);
    if (section) {
      section.scrollIntoView({ behavior: 'smooth' });
      section.classList.add('highlight-section');
      setTimeout(() => section.classList.remove('highlight-section'), 1200);
    }
  }

  htmlSantizer(html: string): SafeHtml {
    if (!html) return this.sanitizer.bypassSecurityTrustHtml('');
    html = html
      .replace(/<(script|style)[^>]*>[\s\S]*?<\/\1>/gi, '')
      .replace(/<p[^>]*>(\s|&nbsp;|<br\s*\/?>|&#8203;|&#xfeff;)*<\/p>/gi, '')
      .replace(
        /<p[^>]*>\s*(<style[^>]*>[\s\S]*?<\/style>|@[\w-]+\s*{[^}]*})\s*<\/p>/gi,
        ''
      )
      .replace(/<\/?font[^>]*>/gi, '')
      .replace(
        /<([a-z]+)[^>]*>(\s|&nbsp;|<br\s*\/?>|&#8203;|&#xfeff;)*<\/\1>/gi,
        ''
      );
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  navigateToBlog(slug: any) {
    if (isPlatformBrowser(this.platformId)) {
      this.router.navigate([`hotel/${slug}`]);
    }
  }

  loadMoreHotels(): void {
    this.fetchHotelsSlug(this.slug);
  }

  getUpcomingHotel() {
    this.destinationService.getUpcomingHotelsList().subscribe({
      next: (response: any) => {
        this.upcominghotels = response || [];
      },
      error: (err: any) => {
        console.error('Failed to fetch blogs:', err);
      },
    });
  }

  getHotelList(slug: string) {
    const KEY = makeStateKey<any>(
      `HOTEL_LIST_${this.destination || 'all'}_${slug || 'noq'}_${this.page}`
    );

    const cached = this.transferState.get(KEY, null);
    if (cached) {
      this.hotelList = cached?.results || [];
      this.cdr.detectChanges();
      this.setHotelListingJsonLd();

      if (isPlatformBrowser(this.platformId)) this.transferState.remove(KEY);
      return;
    }

    this.destinationService.searchHotels(slug, this.page, this.limit).subscribe((res) => {
      this.hotelList = res?.results || [];
      this.cdr.detectChanges();

      this.setHotelListingJsonLd();

      if (!isPlatformBrowser(this.platformId)) {
        this.transferState.set(KEY, res);
      }
    });
  }

  getVideoMimeType(url?: string | null): string {
    if (!url) return 'video/mp4';
    const ext = url.split('.').pop()?.toLowerCase();
    switch (ext) {
      case 'webm':
        return 'video/webm';
      case 'ogg':
      case 'ogv':
        return 'video/ogg';
      case 'mp4':
      default:
        return 'video/mp4';
    }
  }

  onDestinationTabChange(tab: any): void {
    this.scrollListingToTop();

    if (!tab || tab.id === 'all') {
      this.destination = 'all';
      this.selectedDestination = null;
      this.metaTagsService.setDefault(this.getCurrentUrlSafe());
      this.setHotelListingJsonLd();
      return;
    }

    this.destination = tab.id;
    this.getDestinationById(this.destination);
  }


  private reloadHeroVideo(): void {
    if (!isPlatformBrowser(this.platformId)) return;
    if (!this.heroVideo) return;

    const videoEl = this.heroVideo.nativeElement;
    videoEl.load();
    videoEl.play().catch(() => { });
  }

  private applyMetaTagsFromDestination(dest: any) {
    const url = this.getCurrentUrlSafe();

    const title =
      dest?.seo?.title?.trim() || `${dest?.name || 'Hotels'} | TripXL Hotels`;

    const desc =
      dest?.seo?.description?.trim() ||
      `Explore hotels in ${dest?.name || 'this destination'} with TripXL. Browse stays, photos, amenities and availability.`;

    const image = dest?.seo?.image || dest?.coverImage || dest?.thumbnail || '';

    this.metaTagsService.setMetaTags(title, url, desc, image);
  }

  private getCurrentUrlSafe(): string {
    const routeUrl = this.router.url || '';
    return isPlatformBrowser(this.platformId) ? window.location.href : routeUrl;
  }

  private getHotelListingPageUrl(): string {
    const dest = (this.destination || 'all').toString();
    return `${this.LISTING_BASE}/${encodeURIComponent(dest)}`;
  }

  private getDestinationDisplayName(): string {
    return (this.selectedDestination?.name || this.destination || 'All Hotels').toString();
  }

  private setHotelListingJsonLd(): void {
    const pageUrl = this.getHotelListingPageUrl();
    const destinationName = this.getDestinationDisplayName();

    const hotels = Array.isArray(this.hotelList) ? this.hotelList : [];

    const itemListElements = hotels
      .filter(Boolean)
      .map((h: any, index: number) => {
        const slug = (h?.slug || '').toString().trim();
        const hotelUrl = slug
          ? `${this.SITE_URL}/our-hotels/${encodeURIComponent(slug)}`
          : pageUrl;

        const image =
          h?.coverImage ||
          h?.banners?.[0]?.imageUrl ||
          h?.thumbnail ||
          '';

        const city =
          h?.cityInfo?.name ||
          h?.city?.name ||
          this.selectedDestination?.name ||
          '';

        const country =
          h?.countryInfo?.name ||
          h?.country?.name ||
          'India';

        return {
          '@type': 'ListItem',
          position: index + 1,
          url: hotelUrl,
          item: {
            '@type': 'Hotel',
            name: h?.name || h?.title || 'Hotel',
            url: hotelUrl,
            image: image ? [image] : undefined,
            address: {
              '@type': 'PostalAddress',
              addressLocality: city || undefined,
              addressCountry: country || undefined,
            },
          },
        };
      });

    const schema = {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          '@id': `${pageUrl}#webpage`,
          url: pageUrl,
          name: `${destinationName} Hotels | TripXL`,
          description: `Browse hotels in ${destinationName}. Compare stays, view photos and book your preferred property.`,
          isPartOf: { '@id': `${this.SITE_URL}/#website` },
        },
        {
          '@type': 'ItemList',
          '@id': `${pageUrl}#itemlist`,
          name: `Hotels in ${destinationName}`,
          itemListOrder: 'https://schema.org/ItemListOrderAscending',
          numberOfItems: itemListElements.length,
          itemListElement: itemListElements,
        },
        {
          '@type': 'BreadcrumbList',
          '@id': `${pageUrl}#breadcrumbs`,
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: `${this.SITE_URL}/` },
            { '@type': 'ListItem', position: 2, name: 'Hotels', item: `${this.LISTING_BASE}/all` },
            { '@type': 'ListItem', position: 3, name: destinationName, item: pageUrl },
          ],
        },
      ],
    };

    const jsonLdId = `hotel-listing-${(this.destination || 'all')}_${(this.slug || 'noq')}`;
    this.jsonLd.setJsonLd(jsonLdId, schema);
  }

  ngOnDestroy(): void {
    this.routerEventsSubscription?.unsubscribe();
    this.metaTagsService.setDefault(this.getCurrentUrlSafe());
  }

  private scrollListingToTop(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const el = document.getElementById(this.blogContainerId);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  getDestinationBySlug(slug: string) {
    this.destinationService.getDestinationDropDown({ country: 'India' }).subscribe({
      next: (res: any) => {
        const destinations = res?.data || [];
        const matchedDestination = destinations.find(
          (d: any) => String(d.slug) === String(slug)
        );

        if (matchedDestination) {
          this.selectedDestination = matchedDestination;
          this.destination = matchedDestination._id;
          this.applyMetaTagsFromDestination(this.selectedDestination);
        } else {
          this.selectedDestination = null;
          this.destination = 'all';
          this.metaTagsService.setDefault(this.getCurrentUrlSafe());
        }

        this.setHotelListingJsonLd();
        this.cdr.detectChanges();
      },
      error: () => {
        this.selectedDestination = null;
        this.destination = 'all';
        this.metaTagsService.setDefault(this.getCurrentUrlSafe());
      },
    });
  }

}
